import 'es6-promise/dist/es6-promise.auto.js';
import 'es6-object-assign/auto';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import {domReady, formatPrice, getDateString, getTimeString} from './Utilities';
import {store} from './store';
import i18n from './i18n';
import {DELETE_CART, INIT_CART, INIT_REGISTRATION_CODES, INIT_SESSION, SET_ENABLE_TICKET_SHOP, SET_LANGUAGE} from './store/commits';
import {TicketList} from './Vue/TicketList/TicketList';
import {Checkout} from './Vue/Checkout/Checkout';
import {CheckoutSuccess} from './Vue/Checkout/CheckoutStates/CheckoutSuccess';
import {CheckoutCancel} from './Vue/Checkout/CheckoutStates/CheckoutCancel';
import {CheckoutFailure} from './Vue/Checkout/CheckoutStates/CheckoutFailure';
import {TicketCode} from './Vue/TicketCode/TicketCode';
import {Cart} from './Vue/Cart/Cart';
import {MiniCart} from './Vue/Cart/MiniCart';
import {TicketRegistration} from './Vue/TicketRegistration/TicketRegistration';
import {Login} from './Vue/Login/Login';
import Vue from 'vue';
import Toasted from 'vue-toasted';
import Spinner from './Vue/Components/Spinner/Spinner.vue';
import {clickOutside} from './Utilities/directives';

if (module.hot) {
  module.hot.accept();

  module.hot.addStatusHandler((status) => {
    if (status === 'prepare') {
      console.clear();
    }
  });
}

let miniCartElement: HTMLElement | null;
const miniCartSelector = '.js-mini-cart';

domReady(async () => {
  const ticketListElement: HTMLElement | null = document.querySelector('.js-ticketshop');
  const checkoutElement: HTMLElement | null = document.querySelector('.js-checkout');
  const checkoutSuccessElement: HTMLElement | null = document.querySelector('.js-checkout-success');
  const checkoutCancelElement: HTMLElement | null = document.querySelector('.js-checkout-cancel');
  const checkoutFailureElement: HTMLElement | null = document.querySelector('.js-checkout-failure');
  const cartElement: HTMLElement | null = document.querySelector('.js-cart');
  miniCartElement = document.querySelector(miniCartSelector);
  const loginElement: HTMLElement | null = document.querySelector('.js-login');
  const registerTicketElement: HTMLElement | null = document.querySelector('.js-register-ticket');
  const dataElements: NodeListOf<HTMLElement> | null = document.querySelectorAll('.js-webshop-data');
  const ticketCodeElement: HTMLElement | null = document.querySelector('.js-ticket-code');

  if (dataElements) {
    const dataSets: Array<any> = [];
    dataElements.forEach((elem) => {
      if (elem.dataset) {
        dataSets.push(elem.dataset);
      }
    });
    const dataObject = Object.assign({}, ...dataSets);
    let storeInstance: any = store;
    storeInstance.commit(INIT_SESSION);
    storeInstance.commit(INIT_CART);
    storeInstance.commit(INIT_REGISTRATION_CODES);
    const enableTicketShop = Boolean(Number.parseInt(dataObject.enableTicketShop, 10));
    storeInstance.commit(SET_ENABLE_TICKET_SHOP, enableTicketShop);
    storeInstance.commit(SET_LANGUAGE, dataObject.language);
    i18n.locale = dataObject.language;

    // only fetch if not on one of these pages, the data is not relevant here anymore
    if (
      !checkoutSuccessElement &&
      !checkoutCancelElement &&
      !checkoutFailureElement
    ) {
      await storeInstance.dispatch('validateSession', {inventory: dataObject.inventory, login: dataObject.login});
      await storeInstance.dispatch('fetchInitialData', {inventoryUrl: dataObject.inventory, cart: storeInstance.getters.getCart});
    }

    if (
      ticketListElement ||
      checkoutElement ||
      checkoutSuccessElement ||
      checkoutCancelElement ||
      checkoutFailureElement ||
      cartElement ||
      miniCartElement ||
      loginElement ||
      registerTicketElement ||
      ticketCodeElement
    ) {
      Vue.prototype.$tm = function (model, field) {
        // {{ $tm(ticketConfiguration.product, 'title') }} --> call in your component
        const currentLang = i18n.locale;
        if (model.translations && model.translations[currentLang]) {
          const translation = model.translations[currentLang][field];
          return translation ? translation : model[field];
        }
        return model[field];
      };

      Vue.use(Toasted);
      Vue.component('spinner', Spinner);
      Vue.directive('clickOutside', clickOutside);

      Vue.filter('formatPrice', function (value) {
        return formatPrice(value);
      });

      Vue.filter('formatTimeString', function (date: Date, language: string) {
        return getTimeString(language, date);
      });

      Vue.filter('formatDateString', function (date: Date, language: string) {
        return getDateString(language, date);
      });

      const enableSentry = JSON.parse(dataObject.enableSentry || 'false');
      if (enableSentry) {
        Sentry.init({
          Vue,
          dsn: 'https://70d7da082bf54ffbb1a77474875abc2f@sentry.andersundsehr.com/46',
          integrations: [
            new BrowserTracing({
              tracingOrigins: ['messe-stuttgart.de'],
            }),
          ],
          // Set tracesSampleRate to 1.0 to capture 100%
          // of transactions for performance monitoring.
          // We recommend adjusting this value in production
          tracesSampleRate: 1.0,
        });
      }

      if (ticketListElement && enableTicketShop) {
        new TicketList(ticketListElement, dataObject, storeInstance, i18n);
      }

      if (checkoutElement && enableTicketShop) {
        new Checkout(checkoutElement, dataObject, storeInstance, i18n);
      }

      if (checkoutSuccessElement && enableTicketShop) {
        new CheckoutSuccess(checkoutSuccessElement, dataObject, storeInstance, i18n);
      }

      if (checkoutCancelElement && enableTicketShop) {
        storeInstance.commit(DELETE_CART);
        new CheckoutCancel();
      }

      if (checkoutFailureElement && enableTicketShop) {
        storeInstance.commit(DELETE_CART);
        new CheckoutFailure();
      }

      if (cartElement && enableTicketShop) {
        new Cart(cartElement, dataObject, storeInstance, i18n);
      }

      if (registerTicketElement && enableTicketShop) {
        new TicketRegistration(registerTicketElement, dataObject, storeInstance, i18n);
      }

      if (loginElement) {
        new Login(loginElement, dataObject, storeInstance, i18n);
      }

      if (ticketCodeElement && enableTicketShop) {
        new TicketCode(ticketCodeElement, dataObject, storeInstance, i18n);
      }

      selectMiniCart(dataObject, storeInstance);
    }

    // dispatch MiniCartCreated event here, if minicartElement was not yet created but should be created
    window.addEventListener('RerenderTicketshop', (e) => {
      selectMiniCart(dataObject, storeInstance);
    });
    const event = new CustomEvent('MiniCartCreated');
    window.dispatchEvent(event);
  }
});

function selectMiniCart(dataObject: any, storeInstance: any) {
  miniCartElement = document.querySelector(miniCartSelector);
  if (miniCartElement) {
    new MiniCart(miniCartElement, dataObject, storeInstance, i18n);
  }
}
